import { createFileRoute } from "@tanstack/react-router";
import { ArticlePage } from "@/components/ArticlePage";
import { articleSchemas } from "@/lib/seo";

const SITE = "https://shopify-freelancer.net";
const PATH = "/ratgeber/shopify-b2b-shop-einrichten";
const TITLE = "Shopify B2B Shop einrichten: Preise, Kunden, Bestellwege";
const DESCRIPTION =
  "B2B auf Shopify: Kundengruppen und Staffelpreise, Netto-Darstellung, Zahlung auf Rechnung, Mindestbestellwerte und Schnellbestellung. Was die Plattform kann und wo Entwicklung nötig wird.";

const faqs = [
  {
    q: "Kann Shopify B2B?",
    a: "Ja, in Grundzügen. Kundengruppen mit eigenen Preislisten, Zahlungsbedingungen und eigene Kataloge sind je nach Tarif direkt vorgesehen, weitergehende Logik entsteht über Apps oder eigene Entwicklung.",
  },
  {
    q: "Wie zeige ich Nettopreise für Geschäftskunden?",
    a: "Über die Steuer- und Kundeneinstellungen lässt sich für gewerbliche Kunden eine Netto-Darstellung einrichten, inklusive Prüfung der Umsatzsteuer-Identifikationsnummer für Lieferungen ins EU-Ausland.",
  },
  {
    q: "Sind Staffelpreise möglich?",
    a: "Ja, über Preislisten je Kundengruppe oder über Mengenregeln. Bei komplexen Konditionen pro Kunde ist meist eine Anbindung an Ihre Warenwirtschaft der sauberere Weg.",
  },
  {
    q: "Geht Kauf auf Rechnung?",
    a: "Zahlungsziele lassen sich für Geschäftskunden hinterlegen, oft in Verbindung mit einem Zahlungsanbieter oder manueller Freigabe. Wichtig ist, vorab zu klären, wer das Ausfallrisiko trägt.",
  },
  {
    q: "Lohnt sich ein getrennter B2B-Shop?",
    a: "Wenn Sortiment, Preise und Ansprache stark auseinandergehen, ist eine Trennung übersichtlicher. Bei überwiegend gleichem Sortiment ist ein Shop mit Kundengruppen meist günstiger im Betrieb.",
  },
];

export const Route = createFileRoute("/ratgeber/shopify-b2b-shop-einrichten")({
  component: Page,
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:url", content: `${SITE}${PATH}` },
      { property: "og:type", content: "article" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: `${SITE}${PATH}` }],
    scripts: articleSchemas({ title: TITLE, description: DESCRIPTION, path: PATH, faqs }),
  }),
});

function Page() {
  return (
    <ArticlePage
      kicker="Ratgeber · B2B"
      title="Shopify B2B Shop einrichten: was die Plattform kann"
      intro="Geschäftskunden bestellen anders: eigene Preise, größere Mengen, Rechnung statt Karte und wenig Geduld für lange Kaufwege. Diese Übersicht zeigt, was in Shopify vorgesehen ist und wo Entwicklung beginnt."
      source="ratgeber-shopify-b2b"
      sections={[
        {
          h: "Kundengruppen und Preise",
          p: [
            "Kern jedes B2B-Shops sind Kunden mit eigenen Konditionen. Shopify bildet das über Firmenkonten und Preislisten ab, je nach Tarif unterschiedlich weitgehend.",
          ],
          list: [
            "Eigene Preisliste oder Rabattsatz pro Kundengruppe",
            "Staffelpreise nach Menge",
            "Eigene Kataloge, damit nicht jeder alles sieht",
            "Zugang erst nach Freigabe statt offener Registrierung",
          ],
        },
        {
          h: "Netto, Steuern und EU-Lieferungen",
          p: [
            "Geschäftskunden erwarten Nettopreise. Dazu gehört die Erfassung und Prüfung der Umsatzsteuer-Identifikationsnummer, damit steuerfreie Lieferungen innerhalb der EU korrekt laufen.",
          ],
        },
        {
          h: "Zahlung und Zahlungsziele",
          p: [
            "Kauf auf Rechnung mit Zahlungsziel ist im B2B üblich. Umsetzbar ist das über hinterlegte Zahlungsbedingungen, einen Anbieter mit Rechnungskauf oder manuelle Freigabe. Klären Sie vorher, wer das Ausfallrisiko trägt.",
          ],
        },
        {
          h: "Bestellwege verkürzen",
          p: [
            "B2B-Kunden bestellen wiederkehrend und in Mengen. Was dort hilft, ist selten Design, sondern Tempo.",
          ],
          list: [
            "Schnellbestellung über Artikelnummer oder Liste",
            "Nachbestellen aus vergangenen Bestellungen",
            "Mengen direkt in der Übersicht ändern",
            "Mindestbestellwert und Verpackungseinheiten sauber abbilden",
          ],
        },
        {
          h: "Wo Entwicklung nötig wird",
          p: [
            "Sobald Konditionen aus Ihrer Warenwirtschaft kommen, Bestellungen dorthin zurückfließen sollen oder Freigabeprozesse mit mehreren Personen nötig sind, reicht der Standard nicht mehr. Dann geht es um Schnittstellen und eigene Logik im Theme.",
          ],
        },
      ]}
      faqs={faqs}
      related={[
        { to: "/shopify-entwickler", label: "Shopify Entwickler buchen" },
        { to: "/shopify-shop-erstellen-lassen", label: "Shop erstellen lassen" },
        { to: "/shopify-e-commerce-loesung", label: "Shopify als E-Commerce-Lösung" },
      ]}
    />
  );
}
